/* eslint-disable jsx-a11y/label-has-for */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactAutoSuggest from 'react-autosuggest';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import SkuSuggestion from './components/SkuSuggestion';

import Icon from '../Icon';
import s from './AutoSuggest.scss';

const renderSuggestion = suggestion => {
  const { label, hint, inStock, emptyStockMessage } = suggestion;
  const usingStock = !!(inStock, emptyStockMessage);
  return (
    <>
      {usingStock ? (
        <SkuSuggestion {...suggestion} />
      ) : (
        <>
          <div>{label}</div>
          {hint && <span>{hint}</span>}
        </>
      )}
    </>
  );
};

const getSuggestionValue = ({ value, currency, label }) => {
  return {
    value,
    currency,
    toString: () => label,
  };
};

const onSuggestionSelected = (event, { method }) => {
  if (method === 'enter') {
    event.preventDefault();
  }
};

class AutoSuggest extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: props.options,
      inputValue: props.value.toString(),
      inputValueLabel: props.valueLabel,
      propValue: props.valueLabel,
      placeHolder: props.placeHolder,
    };
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(
      this,
    );
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(
      this,
    );
    this.onValueChange = this.onValueChange.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    // Need to set correct value if prop value changes
    if (props.valueLabel !== state.propValue) {
      return {
        inputValue: props.value.toString(),
        inputValueLabel: props.valueLabel,
        propValue: props.valueLabel,
      };
    }
    return null;
  }

  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionsFetchRequested = ({ value, reason }) => {
    const { options } = this.props;
    this.setState({
      suggestions:
        value && reason !== 'input-focused'
          ? options.filter(suggestion =>
              suggestion.value.toString().startsWith(value.trim()),
            )
          : options,
    });
  };

  onValueChange(inputValue) {
    // Product sets onchange on each option, basket on component
    const { options, onChange } = this.props;
    let option;
    if (typeof inputValue === 'object') {
      // When selecting by enter, touch or click
      option = options.find(
        ({ currency, value }) =>
          value === inputValue.value && currency === inputValue.currency,
      );
    } else if (typeof inputValue === 'string') {
      // When typing, only allow selection of unique values
      const numericValue = parseInt(inputValue.trim(), 10);
      if (!Number.isNaN(numericValue)) {
        const filteredOptions = options.filter(
          ({ value }) => value === numericValue,
        );
        option = filteredOptions.length === 1 ? filteredOptions[0] : null;
      }
    }
    if (option && onChange) {
      onChange(option);
    }
    if (option && typeof option.onChange === 'function') {
      option.onChange(option.value);
    }
  }

  render() {
    const {
      suggestions,
      inputValue,
      inputValueLabel,
      placeHolder,
    } = this.state;
    const theme = {
      suggestionsContainer: s.container,
      suggestionsContainerOpen: s['container--open'],
      input: '',
      inputOpen: s['input--open'],
      inputFocused: '',
      container: '',
      containerOpen: '',
      suggestionsList: s.suggestionsList,
      suggestion: s.suggestion,
      suggestionFirst: '',
      suggestionHighlighted: s['suggestion--highlighted'],
      sectionContainer: '',
      sectionContainerFirst: '',
      sectionTitle: '',
    };

    const {
      label,
      showLabel,
      value,
      valueLabel,
      type,
      disabled,
      icon,
      hasError,
      fluid,
      onChange,
      rootClassName,
      innerRef,
      autoFocus,
      options,
      id,
      name,
      ...props
    } = this.props;
    const classes = cx(
      s.root,
      disabled && s.disabled,
      s.hasIcon,
      fluid && s.fluid,
      hasError && s.hasError,
      rootClassName && rootClassName,
    );
    const labelClass = cx(
      showLabel ? s.label : 'visually-hidden',
      props.required && s.required,
    );

    const inputProps = {
      type,
      disabled,
      value: inputValueLabel || inputValue,
      placeholder: placeHolder,
      id,
      name,
      required: props.required,
      onChange: (e, { newValue, method }) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({
          inputValue: newValue,
        });
        if (['enter', 'click'].includes(method)) {
          this.onValueChange(newValue);
        }
      },
      onBlur: () => {
        this.onValueChange(inputValue);
        this.setState({
          inputValue: value.toString(),
          inputValueLabel: valueLabel,
          placeHolder: props.placeHolder,
        });
      },
      onFocus: () => {
        this.setState({
          inputValue: '',
          inputValueLabel: '',
          placeHolder: inputValue,
        });
      },
    };

    return (
      <label className={classes}>
        {label && <span className={labelClass}>{label}</span>}
        {icon && <Icon name={icon} className={s.icon} />}
        <svg width="10" height="5" className={cx(s.icon, s.iconRight)}>
          <path d="M0 0h10L5 5z" />
        </svg>
        <ReactAutoSuggest
          id={id}
          suggestions={suggestions}
          shouldRenderSuggestions={
            () => true /* Needed to display suggestions after focus */
          }
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          theme={theme}
          focusInputOnSuggestionClick={false}
        />

        {hasError && <span className={s.error}>{hasError.message}</span>}
      </label>
    );
  }
}

AutoSuggest.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueLabel: PropTypes.string,
  label: PropTypes.node,
  showLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  icon: PropTypes.string,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  multiline: PropTypes.bool,
  fluid: PropTypes.bool,
  onChange: PropTypes.func,
  rootClassName: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape()]),
  autoFocus: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  placeHolder: PropTypes.string,
};

AutoSuggest.defaultProps = {
  value: '',
  valueLabel: undefined,
  label: undefined,
  showLabel: false,
  type: 'text',
  disabled: false,
  icon: undefined,
  hasError: false,
  multiline: false,
  fluid: false,
  rootClassName: undefined,
  autoFocus: false,
  onChange: undefined,
  innerRef: null,
  id: undefined,
  name: undefined,
  required: false,
  placeHolder: '',
};

export default withStyles(s)(AutoSuggest);
